import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import './Slider.css';

const Slider = ({
  label,
  title,
  description,
  image,
  price,
  originalPrice,
  onViewProduct,
  onAddToCart,
  sliderData = [],
  currentIndex,
  setCurrentIndex,
}) => {
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const imageRef = useRef(null);
  const labelRef = useRef(null);
  const buttonRef = useRef(null);
  const priceRef = useRef(null);
  const originalPriceRef = useRef(null);

  const autoScrollRef = useRef();

  // 🟢 Get current slide data safely
  const current = sliderData[currentIndex] || {};

  // 🔁 Animation on slide change
  useEffect(() => {
    if (!titleRef.current || !descRef.current || !imageRef.current) return;

    const tl = gsap.timeline({
      defaults: { ease: 'power2.out', duration: 0.8 },
    });

    tl.fromTo(titleRef.current, { y: -50, opacity: 0 }, { y: 0, opacity: 1 }, 0)
      .fromTo(descRef.current, { y: -30, opacity: 0 }, { y: 0, opacity: 1 }, 0.2)
      .fromTo(imageRef.current, { scale: 0.85, opacity: 0 }, { scale: 1, opacity: 1 }, 0.4)
      .fromTo(labelRef.current, { y: 30, opacity: 0 }, { y: 0, opacity: 1 }, 0.6)
      .fromTo(buttonRef.current, { y: 30, opacity: 0 }, { y: 0, opacity: 1 }, 0.7)
      .fromTo(priceRef.current, { y: 30, opacity: 0 }, { y: 0, opacity: 1 }, 0.8);

    if (originalPrice && originalPriceRef.current) {
      tl.fromTo(originalPriceRef.current, { y: 30, opacity: 0 }, { y: 0, opacity: 1 }, 0.9);
    }
  }, [currentIndex, originalPrice]);

  // 🕐 Auto-scroll function reference
  useEffect(() => {
    autoScrollRef.current = () => {
      setCurrentIndex((prevIndex) =>
        prevIndex === sliderData.length - 1 ? 0 : prevIndex + 1
      );
    };
  }, [sliderData.length, setCurrentIndex]);

  // 🕒 Trigger auto-scroll every 5 seconds
  useEffect(() => {
    if (sliderData.length <= 1) return;

    const interval = setInterval(() => {
      if (autoScrollRef.current) autoScrollRef.current();
    }, 5000);

    return () => clearInterval(interval);
  }, [sliderData.length]);

  return (
    <div
      className="new-slider-ecom"
      style={{
        background: current.background_image
          ? `url(${current.background_image})`
          : 'none',
      }}
    >
      {/* Dots Navigation */}
      <div className="new-slider-product-dots">
        {sliderData.map((_, index) => (
          <span
            key={index}
            className={`slider-dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
          ></span>
        ))}
      </div>

      {/* Top Content */}
      <div className="new-slider-product-top-content">
        <h2 ref={titleRef}>{title}</h2>
        <p ref={descRef}>{description}</p>
      </div>

      {/* Image */}
      <div className="new-slider-product-image" ref={imageRef}>
        <img src={image} alt={title} />
      </div>

      {/* Bottom Content */}
      <div className="new-slider-product-bottom-content">
        <h4 ref={labelRef}>{label}</h4>
        <button ref={buttonRef} onClick={onViewProduct}>
          View Product
        </button>
      </div>

      {/* Price Section */}
      <div className="new-slider-product-prices">
        <h4 ref={priceRef}>₹{price}</h4>
        {originalPrice && (
          <p ref={originalPriceRef}>
            <del>₹{originalPrice}</del>
          </p>
        )}
      </div>
    </div>
  );
};

export default Slider;
